/**
 * Data layer for `nl-query-report-interface-ai-native`. Thin typed fetch wrappers over /api/v1/nl-query-report-interface-ai-native.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface NlQueryReportInterfaceAiNativeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListNlQueryReportInterfaceAiNativeResponse {
	count: number;
	items: NlQueryReportInterfaceAiNativeRow[];
}

export interface CreateNlQueryReportInterfaceAiNativeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/nl-query-report-interface-ai-native";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListNlQueryReportInterfaceAiNativeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListNlQueryReportInterfaceAiNativeResponse>(res);
}

export async function create(body: CreateNlQueryReportInterfaceAiNativeBody): Promise<NlQueryReportInterfaceAiNativeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<NlQueryReportInterfaceAiNativeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const nlQueryReportInterfaceAiNativeQueryKeys = {
	all: ["nl-query-report-interface-ai-native"] as const,
	list: () => [...nlQueryReportInterfaceAiNativeQueryKeys.all] as const,
	detail: (id: string) => [...nlQueryReportInterfaceAiNativeQueryKeys.all, id] as const,
};
